import { CircleOutlined } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Clear";
import { Box, Button, CircularProgress, Table, TableBody, TableCell, TableContainer, TableRow, } from "@mui/material";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import { ReactComponent as EditIcon } from "../../../../common/icons/editFilled.svg";
import {
  SupplementsData,
  VariationSpecificSettings,
} from "../../../../features/supplements/supplementAPI";
import useSupplementDefinitions from "../../../../features/supplements/useSupplementDefinitions";
import i18n from "../../../../i18n";
import AddButton from "../addButton/AddButton";
import DialogComponent from "../dialogs/DialogComponent";
import EditSupplementsModal from "./EditSupplementsModal";

interface Props {
  areTesVariationsValid?: boolean; // TODO: remove optionalilty?
  tesId: string;
  tesSelections: [string, string][];
  date: number | null;
  supplements: SupplementsData;
  isLoading: boolean;
  setIsLoading?: (isLoading: boolean) => void;
  onEdit: (key: string, mode: string) => Promise<boolean> | boolean;
  onAdd: (key: string, mode: string) => Promise<boolean> | boolean;
  setPeriodStartDateIsRequired?: Dispatch<SetStateAction<boolean | null>>;
  handleVariationSpecificSettings?: (data: VariationSpecificSettings) => void;
  onDelete: (key: string) => void;
  modalTitle: string;
  modalSupplementSelectLabel: string;
  modalSupplementOptionalityLabel: string;
  tableWidth?: string;
  editable?: boolean;
  canEdit?: boolean;
}

const Supplements = ({
  areTesVariationsValid, // TODO: remove optionalilty?
  tesId,
  tesSelections,
  date,
  supplements,
  isLoading,
  setIsLoading,
  onEdit,
  onAdd,
  onDelete,
  setPeriodStartDateIsRequired,
  handleVariationSpecificSettings,
  modalTitle,
  modalSupplementSelectLabel,
  modalSupplementOptionalityLabel,
  tableWidth,
  editable,
  canEdit = true, // TODO: remove default value or change to false after access rights are handled in every view
}: Props) => {
  const [originalSupplement, setOriginalSupplement] = useState<{
    key: string;
    mode: string;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const saveInProgress = useRef(false);
  const isBusy = isLoading || isSaving;

  const { t } = useTranslation();

  const translatedSupplementNames = useAppSelector(
    (state) => state.localized.supplement,
  );

  const { supplementDefinitions, areDefinitionsLoading } =
    useSupplementDefinitions({
      tesId,
      date,
      tesSelections,
      areTesVariationsValid,
      setIsLoading,
      setPeriodStartDateIsRequired,
      handleVariationSpecificSettings,
    });

  const handleOnSave = async (key: string, mode: string) => {
    if (saveInProgress.current || isLoading) return;

    // Guard immediately, including clicks before React renders the loading state.
    saveInProgress.current = true;
    setIsSaving(true);
    try {
      // If key already exists, edit it. Otherwise call the add function.
      const isSuccess =
        originalSupplement?.key === key
          ? await onEdit(key, mode)
          : await onAdd(key, mode);

      if (isSuccess) {
        // Closes modal
        setOriginalSupplement(null);
      } else {
        // TODO: Show error message
      }
    } catch (error) {
      console.error("Error:", error);
      // TODO: Handle error or show error message
    } finally {
      saveInProgress.current = false;
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (saveInProgress.current) return;
    setOriginalSupplement(null);
  };

  // Create an array of supplement keys and translated names
  const supplementArray = Object.keys(supplements).map((key) => ({
    key,
    name:
      translatedSupplementNames[key] !== undefined
        ? translatedSupplementNames[key]
        : key,
  }));

  // Sort the array by translated name with the locale
  supplementArray.sort((a, b) => {
    const nameA = a.name || ""; // Use empty string if a.name is undefined
    const nameB = b.name || ""; // Use empty string if b.name is undefined
    return nameA.localeCompare(nameB, i18n.language);
  });

  const showActions = editable !== false && canEdit;
  const canModify = showActions && !isBusy;
  const showAddButton = showActions;
  const disableAddButton = Object.keys(supplementDefinitions).length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: tableWidth || "unset",
      }}
    >
      <TableContainer>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={{ padding: "6px" }}>
                {t("talentProfile:benefits")}
              </TableCell>
              <TableCell sx={{ padding: "6px" }}>
                {t("talentProfile:optionality")}
              </TableCell>
              <TableCell sx={{ padding: "6px" }} />
              <TableCell sx={{ padding: "6px" }} />
            </TableRow>
            {supplementArray.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {t("talentProfile:noSupplementsToShow")}
                </TableCell>
              </TableRow>
            ) : (
              supplementArray.map((supplement, index) => (
                <TableRow key={supplement.key + index}>
                  <TableCell sx={{ fontWeight: 600, padding: "6px" }}>
                    {supplement.name}
                  </TableCell>
                  <TableCell sx={{ padding: "6px" }}>
                    {supplements[supplement.key].mode !== "DEFAULT_OFF" ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <CheckIcon
                          sx={{
                            marginRight: "5px",
                          }}
                        />
                        {supplements[supplement.key].mode === "FORCE"
                          ? t("common:FORCE")
                          : t("common:DEFAULT_ON")}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <CircleOutlined
                          sx={{
                            marginRight: "5px",
                          }}
                        />
                        {t("common:DEFAULT_OFF")}
                      </Box>
                    )}
                  </TableCell>

                  <TableCell sx={{ padding: "6px" }}>
                    {showActions &&
                      supplements[supplement.key]?.definition?.type ===
                        "ON_OFF" && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            color: "rgba(0, 0, 0, 0.52)",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            if (!canModify) return;
                            setOriginalSupplement({
                              key: supplement.key,
                              mode: supplements[supplement.key].mode,
                            });
                          }}
                        >
                          <EditIcon style={{ marginRight: "10px" }} />
                          {t("common:edit")}
                        </Box>
                      )}
                  </TableCell>
                  <TableCell sx={{ padding: "6px" }}>
                    {showActions && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                          color: "rgba(0, 0, 0, 0.52)",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          if (!canModify) return;
                          onDelete(supplement.key);
                        }}
                      >
                        <DeleteIcon
                          sx={{
                            marginRight: "5px",
                          }}
                        />
                        {t("common:delete")}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box minHeight="16px">
        {showAddButton &&
          (!areDefinitionsLoading ? (
            <AddButton
              variant="outlined"
              sx={{
                margin: "16px 0px 32px 0px",
                alignSelf: "flex-start",
              }}
              label={t("talentProfile:addSupplement")}
              onClick={() => setOriginalSupplement({ key: "", mode: "" })}
              disabled={disableAddButton || !canModify}
            />
          ) : (
            <Button
              variant="outlined"
              disabled={true}
              sx={{
                margin: "16px 0px 32px 0px",
                borderRadius: "15px",
              }}
            >
              <CircularProgress
                size={20}
                sx={{
                  margin: "0px 16px",
                }}
              />
            </Button>
          ))}
      </Box>
      <DialogComponent
        open={!!originalSupplement && !!canEdit}
        onClose={handleClose}
        autoWidth
        DialogContentProps={{ sx: { padding: "0px" } }}
      >
        <EditSupplementsModal
          supplements={supplements}
          originalSupplement={originalSupplement}
          isLoading={isBusy}
          handleOnSave={handleOnSave}
          onClose={handleClose}
          modalTitle={modalTitle}
          modalSupplementSelectLabel={modalSupplementSelectLabel}
          modalSupplementOptionalityLabel={modalSupplementOptionalityLabel}
          supplementDefinitions={supplementDefinitions}
        />
      </DialogComponent>
    </Box>
  );
};

export default Supplements;
